/**
 * Sensus — Recherche « bulle » du header (ordinateur).
 * Comportement repris de l'ancien site (search-popup) :
 *  - clic sur la loupe : le champ se déploie, le curseur s'y place après l'animation ;
 *  - clic à l'extérieur ou Échap : fermeture ;
 *  - saisie : résultats prédictifs (API Shopify) affichés sous le champ.
 */

const DEBOUNCE_MS = 300;

/** @param {HTMLDetailsElement} bubble */
function setupBubble(bubble) {
  if (bubble.dataset.ready) return;
  bubble.dataset.ready = 'true';

  const input = bubble.querySelector('[data-search-bubble-input]');
  const results = bubble.querySelector('[data-search-bubble-results]');
  const status = bubble.querySelector('[data-search-bubble-status]');
  if (!(input instanceof HTMLInputElement) || !(results instanceof HTMLElement)) return;

  /** @type {AbortController | null} */
  let controller = null;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  let timer;

  const hideResults = () => {
    results.hidden = true;
    results.innerHTML = '';
  };

  const search = async () => {
    const terms = input.value.trim();
    if (!terms) {
      hideResults();
      return;
    }

    controller?.abort();
    controller = new AbortController();

    const base = window.Theme?.routes?.predictive_search_url || `${window.Shopify?.routes?.root || '/'}search/suggest`;
    const url = new URL(base, location.origin);
    url.searchParams.set('q', terms);
    url.searchParams.set('section_id', 'sensus-predictive-search');
    url.searchParams.set('resources[type]', 'product,collection,page,article');
    url.searchParams.set('resources[limit]', '6');

    try {
      const response = await fetch(url, { signal: controller.signal });
      if (!response.ok) throw new Error(String(response.status));
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const content = doc.querySelector('.predictive-search-results');
      if (!content || input.value.trim() !== terms) return;
      results.innerHTML = '';
      results.append(content);
      results.hidden = false;
      if (status) status.textContent = content.textContent?.trim().slice(0, 120) || '';
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      hideResults();
    }
  };

  input.addEventListener('input', () => {
    clearTimeout(timer);
    timer = setTimeout(search, DEBOUNCE_MS);
  });

  bubble.addEventListener('toggle', () => {
    if (bubble.open) {
      setTimeout(() => input.focus(), 250);
      if (input.value.trim()) search();
    } else {
      hideResults();
    }
  });
}

function init() {
  document.querySelectorAll('[data-search-bubble]').forEach((bubble) => {
    if (bubble instanceof HTMLDetailsElement) setupBubble(bubble);
  });
}

document.addEventListener('click', (event) => {
  document.querySelectorAll('[data-search-bubble][open]').forEach((bubble) => {
    if (event.target instanceof Node && !bubble.contains(event.target)) bubble.removeAttribute('open');
  });
});

document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;
  document.querySelectorAll('[data-search-bubble][open]').forEach((bubble) => {
    bubble.removeAttribute('open');
    bubble.querySelector('summary')?.focus();
  });
});

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
else init();
document.addEventListener('shopify:section:load', init);
